import {defineStore} from 'pinia';
import {computed, nextTick, ref, watch} from 'vue';
import ChatSession from "@src/types/ChatSession";
import ChatMessage from "@src/types/ChatMessage";
import Group from "@src/types/Group";
import {useMessages} from "@src/composables/useMessages";
import {Component} from "@vue/runtime-core";
import MainArea from "@src/components/views/HomeView/MainArea/MainArea.vue";
import {parsePhoneNumberFromString} from 'libphonenumber-js/min';
import {useMessageStore} from "@src/stores/messages";
import GroupMessageService from "../services/groupMessage"
import BotMessageService from "../services/botMessage"
import GroupMessage from "@src/types/GroupMessage";
import useStore from "@src/stores/store";
import {useChatSessions} from "@src/composables/useChatSessions";

export const useChatSessionStore = defineStore('chatsession', () => {
    // Estado: Apenas o ID
    const activeSessionId = ref<number | null>(null);
    const selectedSession = ref<ChatSession | undefined>(undefined);
    const sessions = ref<ChatSession[]>([]);
    const currentComponent = ref<Component>(MainArea);
    const replyMessage = ref<ChatMessage | GroupMessage | undefined>(undefined);
    const container = ref()
    const openFiltersSelector = ref(false);
    const openOptionsSelector = ref(false);
    const openAttachSelector = ref(false);
    const type = ref<string | undefined>('session')
    const openMediaPane = ref<boolean>(false);
    const showContactPane = ref<boolean>(false);
    const chatSessionComposable = useChatSessions();
    const holdSessions = chatSessionComposable.getSessionsByStatus('hold')
    const openActionsBulk = ref<boolean>(false);
    const actionSessions = ref<number[]>([])
    const filterTagsSelected = ref<number[]>([])
    const filterKanbanSelected = ref<number[]>([])
    const filterChannelsSelected = ref<number[]>([])
    const filterUnread = ref<boolean>(false)
    const isUserScrolling = ref(false);
    const scrollReady = ref(false);
    const isLoadingSession = ref(false)
    const showArchivedSessions = ref(false)
    let stopMessagesWatcher: (() => void) | null = null;

    //Start conversation
    const openStartConversationModal = ref(false)
    const selectedCostumer = ref({
        id: 0,
        name: '',
        token: ''
    })

    // Edit message
    const inputMode = ref<string>('normal');
    const selectedMessage = ref<ChatMessage | undefined>(undefined);

    //Forward declarations
    const MAX_FORWARD_TARGETS = 5;
    const forwardTargetSessions = ref<ChatSession[]>([]);
    const isForwardingMessages = ref(false);
    const forwardTargetSessionsCount = computed(() => forwardTargetSessions.value.length);

    const forwardMode = ref(false);
    const forwardMessages = ref<(ChatMessage | GroupMessage)[]>([]);
    const openForwardSessionsModal = ref(false);
    const forwardMessagesCount = computed(() => forwardMessages.value.length);

    //forward methods
    function isTargetSessionSelected(sessionId: number) {
        return forwardTargetSessions.value.some(session => session.id === sessionId);
    }

    function canSelectMoreTargetSessions(sessionId?: number) {
        if (sessionId && isTargetSessionSelected(sessionId)) return true;
        return forwardTargetSessions.value.length < MAX_FORWARD_TARGETS;
    }

    function toggleForwardTargetSession(session: ChatSession) {
        const exists = forwardTargetSessions.value.find(item => item.id === session.id);

        if (exists) {
            forwardTargetSessions.value = forwardTargetSessions.value.filter(
                item => item.id !== session.id
            );
            return;
        }

        if (forwardTargetSessions.value.length >= MAX_FORWARD_TARGETS) {
            return;
        }

        forwardTargetSessions.value.push(session);
    }

    function clearForwardTargetSessions() {
        forwardTargetSessions.value = [];
    }

    function canForwardMessage(message: ChatMessage | GroupMessage) {
        let wrongType = message.type !== 'video' && message.type !== 'warning'
        let limitExceeded = forwardMessages.value.length < MAX_FORWARD_TARGETS || isMessageSelectedForForward(message.id)
        return wrongType && message.from !== 'system' && message.status !== '5' && limitExceeded;
    }

    function isMessageSelectedForForward(messageId: number | string | undefined) {
        return forwardMessages.value.some((msg) => String(msg.id) === String(messageId));
    }

    function startForwardMode(initialMessage?: ChatMessage | GroupMessage) {
        forwardMode.value = true;
        replyMessage.value = undefined;
        inputMode.value = 'normal';
        selectedMessage.value = undefined;
        openAttachSelector.value = false;
        openForwardSessionsModal.value = false;
        forwardTargetSessions.value = [];
        forwardMessages.value = [];

        if (initialMessage && canForwardMessage(initialMessage)) {
            forwardMessages.value = [initialMessage];
        }
    }

    function stopForwardMode() {
        forwardMode.value = false;
        forwardMessages.value = [];
        forwardTargetSessions.value = [];
        openForwardSessionsModal.value = false;
        isForwardingMessages.value = false;
    }

    function toggleForwardMessage(message: ChatMessage | GroupMessage) {
        if (!canForwardMessage(message)) return;

        const exists = forwardMessages.value.find(
            (msg) => String(msg.id) === String(message.id)
        );

        if (exists) {
            forwardMessages.value = forwardMessages.value.filter(
                (msg) => String(msg.id) !== String(message.id)
            );
            return;
        }

        forwardMessages.value.push(message);
    }

    function openForwardModal() {
        if (!forwardMessages.value.length) return;
        openForwardSessionsModal.value = true;
    }

    function closeForwardModal() {
        openForwardSessionsModal.value = false;
        forwardTargetSessions.value = [];
    }

    //end forward methods

    const clearFilters = () => {
        filterKanbanSelected.value = [];
        filterTagsSelected.value = [];
        filterChannelsSelected.value = []
        filterUnread.value = false
    }
    const toggleKanban = (kanbanId: number) => {
        const index = filterKanbanSelected.value.indexOf(kanbanId)

        if (index === -1) {
            // adiciona
            filterKanbanSelected.value.push(kanbanId)
        } else {
            // remove
            filterKanbanSelected.value.splice(index, 1)
        }
    }

    const toggleChannel = (channelId: number) => {
        const index = filterChannelsSelected.value.indexOf(channelId)
        if (index === -1) {
            //adiciona
            filterChannelsSelected.value.push(channelId)
        } else {
            //remove
            filterChannelsSelected.value.splice(index, 1)
        }
    }

    const isKanbanSelected = (kanbanId: number) => {
        return filterKanbanSelected.value.includes(kanbanId)
    }

    const isChannelSelected = (channelId: number) => {
        return filterChannelsSelected.value.includes(channelId)
    }
    const toggleTag = (tagId: number) => {
        const index = filterTagsSelected.value.indexOf(tagId)

        if (index === -1) {
            // adiciona
            filterTagsSelected.value.push(tagId)
        } else {
            // remove
            filterTagsSelected.value.splice(index, 1)
        }
    }
    const isTagSelected = (tagId: number) => {
        return filterTagsSelected.value.includes(tagId)
    }

    function toggleActionSession(id: number) {
        const index = actionSessions.value.indexOf(id)

        if (index === -1) {
            // adiciona
            actionSessions.value.push(id)
        } else {
            // remove
            actionSessions.value.splice(index, 1)
        }
    }

    function toggleAllActionSessions(ids: number[]) {

        const allSelected =
            actionSessions.value.length === ids.length

        if (allSelected) {
            actionSessions.value = []
        } else {
            actionSessions.value = [...ids]
        }
    }


    async function setActiveSession(id: number) {
        activeSessionId.value = id;
        selectedSession.value = sessions.value.find(s => s.id === id)
        scrollDown()
    }

    function updateMessageProgress(tempId: number, progress: number) {
        const message = selectedSession.value?.messages.find(msg => {
            return msg.id == tempId;
        });

        if (message) {
            message.progress = progress;
        }
    }

    function replaceTemporaryMessage(tempId: any) {
        const messages = selectedSession.value?.messages;
        const tempIndex = messages?.findIndex(msg => msg.id === tempId);
        if (tempIndex !== -1 && tempIndex !== undefined) {
            if (tempIndex !== -1) {
                selectedSession.value?.messages?.splice(tempIndex, 1);
            }
            return;
        }
    }

    function setStartConversationData(data: { id: number, name: string, token: string }) {
        openStartConversationModal.value = true
        selectedCostumer.value = data
    }

    async function scrollDown() {
        openAttachSelector.value = false;
        isUserScrolling.value = false;
        scrollReady.value = false;
        await nextTick();
        const el = container.value;
        if (el) {
            el.scrollTo({top: el.scrollHeight, behavior: 'instant'});

            // Re-scroll after media (images/videos) finishes loading
            const media = Array.from(el.querySelectorAll('img, video')) as (HTMLImageElement | HTMLVideoElement)[];
            const pending = media.filter(m => {
                if (m instanceof HTMLImageElement) return !m.complete;
                if (m instanceof HTMLVideoElement) return m.readyState < 1;
                return false;
            });
            if (pending.length > 0) {
                let loaded = 0;
                const onLoad = () => {
                    loaded++;
                    if (loaded >= pending.length) {
                        el.scrollTo({top: el.scrollHeight, behavior: 'instant'});
                    }
                };
                pending.forEach(m => {
                    m.addEventListener('load', onLoad, {once: true});
                    m.addEventListener('loadedmetadata', onLoad, {once: true});
                    m.addEventListener('error', onLoad, {once: true});
                });
            }
        }
        scrollReady.value = true;
    }

    async function scrollDownGroup(force = false) {
        openAttachSelector.value = false;

        const el = container.value as HTMLElement | null;
        if (!el) return;

        // se quiser: só auto-scroll quando o usuário já está perto do final
        const nearBottom =
            el.scrollTop + el.clientHeight >= el.scrollHeight - 120;

        if (!force && !nearBottom) return;

        // espera DOM renderizar (mensagem entrou na lista)
        await nextTick();

        // 2 frames: garante que o browser recalculou scrollHeight
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                el.scrollTo({
                    top: el.scrollHeight,
                    behavior: "smooth",
                });
            });
        });
    }

    function formattedToken(token: string | undefined) {
        if (!token) {
            return
        }

        let tokenToFormat = parseInt(token)
        // util: normaliza pra E.164 (mantém '+')
        const normalizeE164 = (v: number) => {
            if (!v) return '';
            const s = String(v).trim();
            if (s.startsWith('+')) return s.replace(/\s/g, '');
            // se vier só dígitos, aceita assim mesmo; não tenta adivinhar DDI aqui
            return '+' + s.replace(/\D/g, '');
        };

        const e164 = normalizeE164(tokenToFormat);
        const p = parsePhoneNumberFromString(e164);
        if (!p) return e164;

        // nacional: ex. BR => "(13) 98180-6376", US => "(904) 708-4077", CL => "9 3422 1414"
        const nat = p.formatNational()
            .replace(/[()]/g, '')  // tira parênteses do DDD
            .replace(/\s+/g, ' ')  // normaliza espaços
            .trim();

        return `+${p.countryCallingCode} ${nat}`;
    }

    function setChatContainer(el: HTMLElement) {
        container.value = el;
    }

    function scrollToMessage(messageId: number | string) {
        if (!container.value) return;

        const el = container.value.querySelector(
            `[message-id="${messageId}"]`
        ) as HTMLElement | null;

        if (!el) {
            console.error("Mensagem não encontrada", messageId);
            return;
        }

        container.value.scrollTo({
            top: el.offsetTop,
            behavior: "smooth",
        });
    }

    const setComponent = (comp: Component) => {
        currentComponent.value = comp;
    }

    async function selectSession(session: ChatSession | Group) {
        const messageStore = useMessageStore()
        const store = useStore();

        isLoadingSession.value = true;

        // limpa seleção de encaminhamento ao trocar de sessão
        stopForwardMode();

        // Se o operador estava numa visão em tela cheia (Kanban, agendamentos,
        // chat interno), Sidebar e MainArea ficam ocultos (ver HomeView.vue) —
        // volta para uma aba normal para garantir que a conversa selecionada
        // seja exibida em vez de permanecer atrás dessas telas.
        if (['kanban', 'schedule-messages', 'internal-chat'].includes(store.activeSidebarComponent)) {
            const isGroupSession = !!(session as any)?.gid;
            const status = String((session as any)?.status ?? "");
            store.activeSidebarComponent = isGroupSession
                ? 'group'
                : status === 'hold'
                    ? 'holding'
                    : ['bot', 'gpt', 'gemini', 'paused'].includes(status)
                        ? 'bot'
                        : 'active';
        }

        store.details = false;
        openMediaPane.value = false;
        showContactPane.value = false;
        messageStore.message = '';
        replyMessage.value = undefined;
        messageStore.groupOldestId = null;
        // @ts-ignore
        selectedSession.value = session;

        // para o watcher anterior antes de criar um novo
        stopMessagesWatcher?.();
        stopMessagesWatcher = null;

        //limpar estado do grupo
        messageStore.clearGroupState();
        messageStore.clearBotState?.();

        if ((session as any).gid) {
            type.value = 'group';

            GroupMessageService.markAsRead(session.id);

            const resp = await GroupMessageService.getGroupMessagesOld(session.id, messageStore.groupOldestId);

            if (resp?.success) {
                messageStore.groupMessages = resp.data || []
                messageStore.groupOldestId = resp.oldest_id ?? null
                messageStore.groupHasMoreMessages = !!resp.has_more

                // last id carregado
                const last = messageStore.groupMessages[messageStore.groupMessages.length - 1];
                // @ts-ignore
                messageStore.groupLastId = last?.id ?? null;
            } else {
                messageStore.groupMessages = [];
                messageStore.groupOldestId = null;
                messageStore.groupHasMoreMessages = false;
            }
        } else {
            type.value = 'session'

            const st = String((session as any)?.status ?? "");
            const isBot = ["bot", "gpt", "gemini", "paused"].includes(st);

            if (session && typeof session === 'object' && 'has_new_messages' in session && session.has_new_messages) {
                await chatSessionComposable.toggleSessionHasNewMessage(session);
            }

            if (isBot) {
                messageStore.clearBotState?.();
                const resp = await BotMessageService.getBotMessagesOld(session.id, messageStore.botOldestId);

                if (resp?.success) {
                    messageStore.botMessages = resp.data || [];
                    messageStore.botOldestId = resp.oldest_id ?? null;

                    const last = messageStore.botMessages[messageStore.botMessages.length - 1];
                    messageStore.botLastId = last?.id ? Number(last.id) : null;
                } else {
                    messageStore.botMessages = [];
                    messageStore.botOldestId = null;
                    messageStore.botLastId = null;
                }
                setComponent(MainArea);
                isLoadingSession.value = false
                await scrollDownGroup(true);
                return;
            }

            const chatMessagesComposable = useMessages();
            const messagesRef = chatMessagesComposable.getMessagesBySessionId(session.id)

            // @ts-ignore
            stopMessagesWatcher = watch(messagesRef, (newMessages: ChatMessage[]) => {
                if (selectedSession.value) {
                    selectedSession.value.messages = newMessages
                }
            }, {immediate: true})
        }
        setComponent(MainArea)
        isLoadingSession.value = false
        await scrollDown()
    }

    function formatPhone(token: string | undefined) {
        if (!token) {
            return '';
        }
        let phone = token?.replace(/\D/g, '');
        if (phone?.length === 13) {
            return `+${phone.substring(0, 2)} ${phone.substring(2, 4)} ${phone.substring(4, 9)}-${phone.substring(9, 13)}`;
        }
        if (phone?.length === 11) {
            return `+55 ${phone.substring(0, 2)} ${phone.substring(2, 7)}-${phone.substring(7, 11)}`;
        }
        return token;
    }

    function clearSession() {
        selectedSession.value = undefined;
        stopForwardMode();
    }

    return {
        isLoadingSession,
        inputMode,
        selectedMessage,
        activeSessionId,
        isUserScrolling,
        scrollReady,
        setActiveSession,
        clearSession,
        sessions,
        selectedSession,
        selectSession,
        currentComponent,
        setComponent,
        replyMessage,
        formattedToken,
        setChatContainer,
        scrollToMessage,
        openAttachSelector,
        openFiltersSelector,
        openOptionsSelector,
        updateMessageProgress,
        scrollDown,
        scrollDownGroup,
        replaceTemporaryMessage,
        type,
        openMediaPane,
        showContactPane,
        formatPhone,
        holdSessions,
        openActionsBulk,
        actionSessions,
        toggleActionSession,
        toggleAllActionSessions,
        filterTagsSelected,
        filterKanbanSelected,
        toggleTag,
        isTagSelected,
        toggleKanban,
        isKanbanSelected,
        clearFilters,
        openStartConversationModal,
        setStartConversationData,
        selectedCostumer,
        showArchivedSessions,
        // forward
        forwardMode,
        forwardMessages,
        forwardMessagesCount,
        openForwardSessionsModal,
        canForwardMessage,
        isMessageSelectedForForward,
        startForwardMode,
        stopForwardMode,
        toggleForwardMessage,
        openForwardModal,
        closeForwardModal,
        MAX_FORWARD_TARGETS,
        forwardTargetSessions,
        forwardTargetSessionsCount,
        isForwardingMessages,
        isTargetSessionSelected,
        canSelectMoreTargetSessions,
        toggleForwardTargetSession,
        clearForwardTargetSessions,
        filterChannelsSelected,
        filterUnread,
        isChannelSelected,
        toggleChannel
    };
});
